import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Typography, App, Checkbox } from 'antd';
import {
  LockOutlined,
  MailOutlined,
  ArrowRightOutlined,
  CheckCircleOutlined,
  DashboardOutlined,
  ShoppingOutlined,
  AppstoreOutlined,
  TeamOutlined,
  LineChartOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { login } from '../../store/slices/authSlice';
import { LoginForm } from '../../types';
import { AuthSplitLayout } from './AuthSplitLayout';
import { AuthFormCard } from './AuthFormCard';
import {
  AuthHeroBadge,
  AuthHeroTitle,
  AuthHeroDescription,
  AuthHeroHighlights,
  AuthHeroFeatureHeading,
  AuthHeroFeatureList,
} from './authHeroPrimitives';

const { Text } = Typography;

const Login: React.FC = () => {
  const [form] = Form.useForm();
  const [rememberMe, setRememberMe] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading, error, isAuthenticated } = useAppSelector((state) => state.auth);
  const { message } = App.useApp();

  useEffect(() => {
    if (isAuthenticated) navigate('/dashboard');
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    if (savedEmail) {
      form.setFieldsValue({ email: savedEmail });
      setRememberMe(true);
    }
  }, [form]);

  const onFinish = async (values: LoginForm) => {
    try {
      await dispatch(login({ ...values, rememberMe })).unwrap();
      message.success('Login successful!');
      navigate('/dashboard');
    } catch (err) {
      message.error(err as string);
    }
  };

  const highlights = [
    { label: 'Products & services', value: 'You list' },
    { label: 'Leads & enquiries', value: 'From TOC' },
    { label: 'Sales & fulfilment', value: 'You handle' },
  ];

  const features = [
    {
      icon: <ShoppingOutlined />,
      title: 'List what you sell',
      description: 'Publish products on The Old Cart so the right buyers discover you.',
    },
    {
      icon: <AppstoreOutlined />,
      title: 'Services on your profile',
      description: 'Showcase packages; we help you get found—not checkout or delivery.',
    },
    {
      icon: <TeamOutlined />,
      title: 'Leads from The Old Cart',
      description: 'You respond and close the sale—order handling stays with you.',
    },
    {
      icon: <LineChartOutlined />,
      title: 'How you are seen',
      description: 'Tune listings from traction and interest—insight, not order processing.',
    },
  ];

  const hero = (
    <>
      <AuthHeroBadge icon={<DashboardOutlined />}>Vendor dashboard</AuthHeroBadge>
      <AuthHeroTitle>List products and services—get leads from our platform</AuthHeroTitle>
      <AuthHeroDescription>
        The Old Cart is a discovery and lead-generation channel. You showcase what you offer; interested buyers
        connect through us. We don&apos;t process or fulfil orders—you own the conversation and the sale.
      </AuthHeroDescription>
      <AuthHeroHighlights items={highlights} />
      <AuthHeroFeatureHeading>What you&apos;ll manage here</AuthHeroFeatureHeading>
      <AuthHeroFeatureList items={features} />
    </>
  );

  const leftFooter = (
    <>
      <span className="inline-flex items-center gap-1">
        <CheckCircleOutlined className="text-[#4ADE80]" />
        Secure sign-in
      </span>
      <span className="text-white/50">·</span>
      <span className="inline-flex items-center gap-1 text-white/70">
        <SafetyCertificateOutlined className="text-xs text-[#F7941F]" />
        Listings and leads—not order processing
      </span>
    </>
  );

  return (
    <AuthSplitLayout hero={hero} footer={leftFooter}>
      <AuthFormCard title="Welcome back" subtitle="Sign in to manage listings and reply to leads. Sales stay between you and the buyer.">
        <Form
          form={form}
          name="login"
          onFinish={onFinish}
          layout="vertical"
          size="large"
          requiredMark={false}
          className="auth-form-fields-tight"
        >
          <Form.Item
            name="email"
            rules={[
              { required: true, message: 'Please enter your email' },
              { type: 'email', message: 'Enter a valid email' },
            ]}
          >
            <Input
              prefix={<MailOutlined className="text-[#0171BF]" />}
              placeholder="Email address"
              autoComplete="email"
              aria-label="Email address"
              className="!min-h-[2.75rem] rounded-lg border-slate-200 text-[15px] transition-colors hover:border-[#0171BF]/40 focus:border-[#0171BF]"
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[
              { required: true, message: 'Please enter your password' },
              { min: 6, message: 'At least 6 characters' },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined className="text-[#0171BF]" />}
              placeholder="Password"
              autoComplete="current-password"
              aria-label="Password"
              className="!min-h-[2.75rem] rounded-lg border-slate-200 text-[15px] transition-colors hover:border-[#0171BF]/40 focus:border-[#0171BF]"
            />
          </Form.Item>

          <div className="mb-4 flex min-h-[2.5rem] flex-wrap items-center justify-between gap-x-4 gap-y-2">
            <Form.Item name="rememberMe" valuePropName="checked" className="auth-form-item--compact">
              <Checkbox
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="text-slate-700 [&_.ant-checkbox-inner]:rounded-lg"
              >
                <span className="text-sm text-slate-600">Remember me</span>
              </Checkbox>
            </Form.Item>
            <Link
              to="/forgot-password"
              className="text-sm font-medium text-[#0171BF] underline-offset-4 transition-colors hover:text-[#015a9e] hover:underline"
            >
              Forgot password?
            </Link>
          </div>

          {error && (
            <div className="mb-3 flex gap-2 rounded-lg border border-red-200/80 bg-red-50/90 px-3.5 py-2.5" role="alert">
              <Text className="text-sm leading-snug text-red-700">{error}</Text>
            </div>
          )}

          <Form.Item className="auth-form-item--compact">
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              block
              size="large"
              icon={<ArrowRightOutlined />}
              iconPosition="end"
              className="!h-12 !rounded-lg !text-[15px] !font-semibold shadow-md shadow-[#0171BF]/15 transition-[transform,box-shadow] hover:shadow-lg"
            >
              Sign in
            </Button>
          </Form.Item>
        </Form>

        <p className="mt-7 text-center text-sm leading-relaxed text-slate-600 sm:mt-8">
          <span className="whitespace-normal sm:whitespace-nowrap">
            Don&apos;t have an account?{' '}
            <Link
              to="/signup"
              className="font-semibold text-[#0171BF] underline-offset-2 hover:text-[#015a9e] hover:underline"
            >
              Create a vendor account
            </Link>
          </span>
        </p>

        <p className="mt-6 text-center text-xs leading-relaxed text-slate-500 sm:mt-7 sm:text-[13px]">
          By signing in, you agree you have read and accept our{' '}
          <Link to="/terms" className="font-medium text-[#0171BF] underline-offset-2 hover:underline">
            Terms of Service
          </Link>{' '}
          and{' '}
          <Link to="/privacy" className="font-medium text-[#0171BF] underline-offset-2 hover:underline">
            Privacy Policy
          </Link>
          , and that you will use The Old Cart vendor services in line with our marketplace rules for listings and lead
          generation.
        </p>
      </AuthFormCard>
    </AuthSplitLayout>
  );
};

export default Login;
